package features.members.data

import kotlin.js.Promise
import core.firebase.FirestoreClient
import features.members.domain.Member

/**
 * Repository for member database operations
 */
object MemberRepository {
    private val db get() = FirestoreClient.db
    
    fun addMember(teamId: String, member: Member): Promise<Unit> {
        console.log("🔥 MemberRepository.addMember called")
        console.log("  Team ID: $teamId")
        console.log("  Member ID: ${member.id}")
        console.log("  Member name: ${member.name}")
        console.log("  Member email: ${member.email}")
        console.log("  Member role: ${member.role}")
        console.log("  Member joinDate: ${member.joinDate}")
        console.log("  Member quota: ${member.quota}")
        
        // Construct memberData object - js() returns dynamic automatically
        val memberData: dynamic = js("{}")
        memberData.id = member.id
        memberData.name = member.name
        memberData.email = member.email
        memberData.role = member.role
        memberData.quota = member.quota
        
        // Handle nullable joinDate - use current date if null
        memberData.joinDate = js("new Date()")
        
        console.log("📦 memberData prepared:")
        console.log("  id:", memberData.id)
        console.log("  name:", memberData.name)
        console.log("  email:", memberData.email)
        console.log("  role:", memberData.role)
        console.log("  quota:", memberData.quota)
        console.log("  joinDate:", memberData.joinDate)
        
        val docRef = db.collection("teams").doc(teamId).collection("members").doc(member.id)
        console.log("📍 Document path: teams/$teamId/members/${member.id}")
        
        return docRef.set(memberData)
            .then {
                console.log("✅ Member saved to Firestore")
                // Update memberIds array in team
                val teamRef = db.collection("teams").doc(teamId)
                val fieldValue: dynamic = js("firebase.firestore.FieldValue")
                val updateData: dynamic = js("{}")
                updateData.memberIds = fieldValue.arrayUnion(member.id)
                teamRef.update(updateData)
            }
            .catch { error ->
                console.error("❌ Firestore error:", error)
                console.error("Error message:", error.asDynamic().message)
                console.error("Error code:", error.asDynamic().code)
                throw error
            }
    }
    
    fun loadMembers(teamId: String): Promise<Array<Member>> {
        console.log("🔍 MemberRepository.loadMembers called for team:", teamId)
        return db.collection("teams").doc(teamId)
            .collection("members").get()
            .then { snapshot ->
                console.log("📦 Firestore returned", snapshot.size, "members")
                val members = mutableListOf<Member>()
                snapshot.forEach { doc ->
                    val data = doc.data()
                    if (data != null) {
                        console.log("  Member doc:", doc.id, "data:", data)
                        val member = Member(
                            id = doc.id,
                            name = data.asDynamic().name as String,
                            email = data.asDynamic().email as String,
                            role = data.asDynamic().role as String,
                            joinDate = data.asDynamic().joinDate?.toString(),
                            quota = (data.asDynamic().quota as? Int) ?: 12
                        )
                        members.add(member)
                        console.log("  ✅ Member added:", member.name)
                    }
                }
                console.log("✅ Total members loaded:", members.size)
                members.toTypedArray()
            }
            .catch { error ->
                console.error("❌ Error loading members:", error)
                console.error("Error message:", error.asDynamic().message)
                throw error
            }
    }
    
    fun updateMember(teamId: String, memberId: String, role: String): Promise<Unit> {
        return db.collection("teams").doc(teamId)
            .collection("members").doc(memberId)
            .update(js("({ role: role })"))
    }
    
    fun removeMember(teamId: String, memberId: String): Promise<Unit> {
        return db.collection("teams").doc(teamId)
            .collection("members").doc(memberId).delete()
            .then {
                // Remove from memberIds array
                db.collection("teams").doc(teamId).update(js("""({
                    memberIds: firebase.firestore.FieldValue.arrayRemove(memberId)
                })"""))
            }
    }
    
    // Bulk Member Operations
    fun bulkAddMembers(teamId: String, members: List<Member>): Promise<Unit> {
        console.log("📦 Bulk adding ${members.size} members to team $teamId")
        
        // Firestore batch limit is 500 operations
        val batchSize = 500
        val batches = members.chunked(batchSize)
        
        var promise: Promise<Unit> = Promise.resolve(Unit)
        
        batches.forEach { batch ->
            promise = promise.then {
                val firestoreBatch = js("firebase.firestore().batch()")
                val memberIds = js("[]")
                
                batch.forEach { member ->
                    val memberRef = db.collection("teams").doc(teamId)
                        .collection("members").doc(member.id)
                    
                    val memberData = js("""({
                        id: member.id,
                        name: member.name,
                        email: member.email,
                        role: member.role,
                        joinDate: new Date(member.joinDate || new Date()),
                        quota: member.quota
                    })""")
                    
                    firestoreBatch.set(memberRef, memberData)
                    memberIds.push(member.id)
                }
                
                // Commit batch
                firestoreBatch.commit().then {
                    // Update memberIds array in team
                    val teamRef = db.collection("teams").doc(teamId)
                    teamRef.update(js("""({
                        memberIds: firebase.firestore.FieldValue.arrayUnion.apply(null, memberIds)
                    })"""))
                }
            }
        }
        
        return promise.then {
            console.log("✅ Bulk add completed")
        }
    }
    
    fun bulkUpdateMemberRole(teamId: String, memberIds: List<String>, newRole: String): Promise<Unit> {
        console.log("📝 Bulk updating ${memberIds.size} members to role: $newRole")
        
        val batch = js("firebase.firestore().batch()")
        
        memberIds.forEach { memberId ->
            val memberRef = db.collection("teams").doc(teamId)
                .collection("members").doc(memberId)
            batch.update(memberRef, js("({ role: newRole })"))
        }
        
        return batch.commit().then {
            console.log("✅ Bulk role update completed")
        }
    }
    
    fun bulkDeleteMembers(teamId: String, memberIds: List<String>): Promise<Unit> {
        console.log("🗑️ Bulk deleting ${memberIds.size} members")
        
        val batch = js("firebase.firestore().batch()")
        val memberIdsArray = js("[]")
        
        memberIds.forEach { memberId ->
            val memberRef = db.collection("teams").doc(teamId)
                .collection("members").doc(memberId)
            batch.delete(memberRef)
            memberIdsArray.push(memberId)
        }
        
        return batch.commit().then {
            // Remove from memberIds array
            val teamRef = db.collection("teams").doc(teamId)
            teamRef.update(js("""({
                memberIds: firebase.firestore.FieldValue.arrayRemove.apply(null, memberIdsArray)
            })"""))
        }.then {
            console.log("✅ Bulk delete completed")
        }
    }
}
